import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Space, Table, Tag, Typography } from 'antd';
import moment from 'moment';
import { isMobile } from 'react-device-detect';
import { useDispatch } from 'react-redux';
import {
    createCustomerFundTransfer,
    getCustomerFundTransferSources,
    reverseCustomerFundTransfer,
} from '@/redux/actions/receiptActions';
import { checkPermission } from '@/utils/permission';
import { renderMoney } from '@/utils/helpers';
import CustomerSelect from './CustomerSelect';

const makeKey = prefix => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const transferStatus = {
    COMPLETED: { color: 'green', label: 'Đã chuyển' },
    PARTIALLY_REVERSED: { color: 'orange', label: 'Đã hoàn tác một phần' },
    REVERSED: { color: 'default', label: 'Đã hoàn tác toàn bộ' },
};

/** Chuyển quyền sở hữu cọc/số dư; không tạo dòng tiền thu mới. */
export default function CustomerFundTransferModal({ visible, customerId, onCancel, onCompleted }) {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const [data, setData] = useState({ deposits: [], balance_sources: [], transfers: [] });
    const [submitting, setSubmitting] = useState(false);
    const canReverse = checkPermission('customer_fund_transfer', 'reverse');
    const hasAvailableSources = [...(data.deposits || []), ...(data.balance_sources || [])]
        .some(row => Number(row.current_balance ?? row.available_amount ?? 0) > 0);

    const load = useCallback(() => dispatch(getCustomerFundTransferSources(customerId)).then(setData), [dispatch, customerId]);
    useEffect(() => {
        if (!visible) return;
        setData({ deposits: [], balance_sources: [], transfers: [] });
        form.resetFields();
        form.setFieldsValue({ effective_at: moment(), idempotency_key: makeKey('fund-transfer') });
        load().catch(() => null);
    }, [visible, customerId, form, load]);

    const submit = values => {
        const recipientId = Number(values.to_customer_id?.value ?? values.to_customer_id);
        const selected = [];
        (values.deposit_ids || []).forEach(id => selected.push({
            source_type: 'DEPOSIT', source_deposit_id: Number(id),
            amount: Number((values.deposit_amounts || {})[id] || 0)
        }));
        (values.balance_ids || []).forEach(id => selected.push({
            source_type: 'CUSTOMER_BALANCE', source_balance_entry_id: Number(id),
            amount: Number((values.balance_amounts || {})[id] || 0)
        }));
        setSubmitting(true);
        return dispatch(createCustomerFundTransfer({
            from_customer_id: customerId, to_customer_id: recipientId,
            effective_at: values.effective_at.format('YYYY-MM-DD HH:mm:ss'), reason: values.reason,
            idempotency_key: values.idempotency_key, sources: selected
        }))
            .then(async () => { await load(); if (onCompleted) await onCompleted(); form.resetFields(); onCancel(); })
            .finally(() => setSubmitting(false));
    };

    const handleSourceChange = (amountsName, rows, selectedIds) => {
        const currentAmounts = form.getFieldValue(amountsName) || {};
        const nextAmounts = {};
        selectedIds.forEach(id => {
            const row = rows.find(item => Number(item.id) === Number(id));
            const available = Number(row?.current_balance ?? row?.available_amount ?? 0);
            const current = Number(currentAmounts[id] || 0);
            nextAmounts[id] = current > 0 && current <= available ? current : available;
        });
        form.setFieldsValue({ [amountsName]: nextAmounts });
    };

    const sourceFields = (idsName, amountsName, rows, emptyText) => <>
        <Form.Item name={idsName} style={{ marginBottom: 12 }}>
            <Select mode="multiple" optionFilterProp="label" onChange={ids => handleSourceChange(amountsName, rows, ids)}
                options={rows.map(row => {
                    const available = Number(row.current_balance ?? row.available_amount ?? 0);
                    return {
                        value: row.id, disabled: available <= 0,
                        label: `${row.receipt_code || 'Khoản tiền chưa có mã phiếu'} · ${available > 0 ? `còn ${renderMoney(available)}` : 'không còn số dư khả dụng'}`
                    };
                })}
                placeholder={rows.length ? 'Chọn một hoặc nhiều nguồn' : emptyText} disabled={!rows.length} />
        </Form.Item>
        <Form.Item shouldUpdate={(before, after) => before[idsName] !== after[idsName]} noStyle>
            {({ getFieldValue }) => (getFieldValue(idsName) || []).map(id => {
                const row = rows.find(item => Number(item.id) === Number(id));
                const max = Number(row?.current_balance ?? row?.available_amount ?? 0);
                return <Form.Item key={id} name={[amountsName, id]} label={`Số tiền từ ${row?.receipt_code || 'khoản tiền đã chọn'}`}
                    extra={`Số tiền còn khả dụng: ${renderMoney(max)}. Có thể nhập số nhỏ hơn để chuyển một phần.`}
                    rules={[{
                        validator: (_, value) => Number(value) > 0 && Number(value) <= max
                            ? Promise.resolve() : Promise.reject(new Error('Số tiền phải lớn hơn 0 và không vượt số dư khả dụng.'))
                    }]}>
                    <InputNumber min={1} max={max} precision={0} style={{ width: '100%' }} formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={value => value.replace(/\./g, '')} />
                </Form.Item>;
            })}
        </Form.Item>
    </>;

    const selectedTotal = values => {
        const depositTotal = (values.deposit_ids || []).reduce((total, id) => total
            + Number((values.deposit_amounts || {})[id] || 0), 0);
        const balanceTotal = (values.balance_ids || []).reduce((total, id) => total
            + Number((values.balance_amounts || {})[id] || 0), 0);
        return depositTotal + balanceTotal;
    };

    const reverse = row => Modal.confirm({
        title: `Hoàn tác phần tiền còn lại của ${row.code}`,
        content: 'Hệ thống chỉ chuyển trả phần tiền bên nhận chưa sử dụng. Phần đã dùng và toàn bộ lịch sử giao dịch được giữ nguyên.',
        onOk: () => dispatch(reverseCustomerFundTransfer(row.id, {
            effective_at: moment().format('YYYY-MM-DD HH:mm:ss'),
            reason: `Hoàn tác phần tiền chưa sử dụng của ${row.code}`, idempotency_key: makeKey('fund-transfer-reversal')
        }))
            .then(async () => { await load(); if (onCompleted) await onCompleted(); }),
    });

    return <Modal visible={visible} destroyOnClose maskClosable={false} width={isMobile ? '100%' : '70%'}
        title="Chuyển tiền cho khách hàng khác" footer={null} onCancel={onCancel}>
        <Alert type="warning" message="Đây là chuyển quyền sở hữu nội bộ, không phải thu/chi tiền và không tự thanh toán công nợ khách nhận." />
        <Form form={form} layout="vertical" onFinish={submit} style={{ marginTop: 16 }}>
            <Row gutter={16}>
                <Col xs={24} md={14}>
                    <Form.Item name="to_customer_id" label="Khách hàng nhận"
                        getValueFromEvent={(value, label) => value ? { value, label } : null}
                        extra="Khách nhận sở hữu khoản tiền sau chuyển; công nợ dịch vụ không tự giảm." rules={[
                            { required: true, message: 'Vui lòng chọn khách hàng nhận.' },
                            {
                                validator: (_, value) => Number(value?.value ?? value) !== Number(customerId)
                                    ? Promise.resolve() : Promise.reject(new Error('Khách nhận phải khác khách gửi.'))
                            },
                        ]}>
                        <CustomerSelect onChange={() => null} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={10}>
                    <Form.Item name="effective_at" label="Thời điểm chuyển"
                        rules={[{ required: true, message: 'Vui lòng chọn thời điểm chuyển.' }]}>
                        <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
            </Row>
            <Card size="small" title="Chọn nguồn tiền cần chuyển" style={{ marginBottom: 16 }} bodyStyle={{ padding: isMobile ? 12 : 16 }}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} md={12}>
                        <div style={{ height: '100%', padding: 12, border: '1px solid #91d5ff', borderRadius: 4, background: '#f0faff' }}>
                            <Space direction="vertical" size={2} style={{ marginBottom: 12 }}>
                                <Tag color="blue">TIỀN CỌC</Tag>
                                <Typography.Text strong>Tiền cọc cần chuyển</Typography.Text>
                                <Typography.Text type="secondary">Chọn từng khoản cọc và nhập số tiền muốn chuyển.</Typography.Text>
                            </Space>
                            {sourceFields('deposit_ids', 'deposit_amounts', data.deposits || [], 'Khách hàng không có tiền cọc khả dụng')}
                        </div>
                    </Col>
                    <Col xs={24} md={12}>
                        <div style={{ height: '100%', padding: 12, border: '1px solid #b7eb8f', borderRadius: 4, background: '#f6ffed' }}>
                            <Space direction="vertical" size={2} style={{ marginBottom: 12 }}>
                                <Tag color="green">SỐ DƯ CỦA KHÁCH HÀNG</Tag>
                                <Typography.Text strong>Số dư chưa phân bổ cần chuyển</Typography.Text>
                                <Typography.Text type="secondary">Chọn từng nguồn số dư và nhập số tiền muốn chuyển.</Typography.Text>
                            </Space>
                            {sourceFields('balance_ids', 'balance_amounts', data.balance_sources || [], 'Khách hàng không có số dư khả dụng')}
                        </div>
                    </Col>
                </Row>
                <Form.Item shouldUpdate={(before, after) => before.deposit_ids !== after.deposit_ids
                    || before.deposit_amounts !== after.deposit_amounts
                    || before.balance_ids !== after.balance_ids
                    || before.balance_amounts !== after.balance_amounts} noStyle>
                    {({ getFieldsValue }) => <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 8,
                        marginTop: 12, padding: '10px 12px', border: '1px solid #91d5ff', borderRadius: 4,
                        background: '#f0faff', textAlign: 'right'
                    }}>
                        <Typography.Text style={{ fontSize: 15, fontWeight: 600, color: '#595959' }}>
                            Tổng tiền chuyển:
                        </Typography.Text>
                        <Typography.Text strong style={{
                            minWidth: 0, fontSize: 20, lineHeight: 1.3, color: '#096dd9', wordBreak: 'break-word'
                        }}>
                            {renderMoney(selectedTotal(getFieldsValue(true)))}
                        </Typography.Text>
                    </div>}
                </Form.Item>
            </Card>
            <Form.Item name="source_validation" dependencies={['deposit_ids', 'balance_ids']}
                style={{ marginTop: -8, marginBottom: 8 }}
                rules={[{
                    validator: () => (form.getFieldValue('deposit_ids') || []).length
                        || (form.getFieldValue('balance_ids') || []).length
                        ? Promise.resolve() : Promise.reject(new Error('Vui lòng chọn ít nhất một nguồn tiền.'))
                }]}>
                <Input type="hidden" />
            </Form.Item>
            <Form.Item name="reason" label="Ghi chú" extra="Nêu căn cứ chuyển tiền để đối chiếu với xác nhận của khách hàng."
                rules={[{ required: true, whitespace: true, message: 'Vui lòng nhập lý do chuyển tiền.' }]}>
                <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item name="idempotency_key" hidden><Input /></Form.Item>
            {!hasAvailableSources && <Alert type="info" message="Khách hàng không có nguồn tiền còn khả dụng để chuyển." style={{ marginBottom: 16 }} />}
            <Space><Button onClick={onCancel}>Hủy</Button><Button type="primary" htmlType="submit" loading={submitting}
                disabled={!hasAvailableSources}>Xác nhận chuyển</Button></Space>
        </Form>
        <Typography.Title level={5} style={{ marginTop: 24 }}>Lịch sử chuyển tiền</Typography.Title>
        <Table rowKey="id" size="small" dataSource={data.transfers || []} pagination={{ pageSize: 5 }}
            scroll={{ x: 950 }} columns={[
                { title: 'Mã', dataIndex: 'code' },
                {
                    title: 'Chiều', render: (_, row) => Number(row.from_customer_id) === Number(customerId)
                        ? `Gửi → ${row.receiver_code} ${row.receiver_name}` : `Nhận ← ${row.sender_code} ${row.sender_name}`
                },
                { title: 'Cọc', dataIndex: 'deposit_amount', align: 'right', render: renderMoney },
                { title: 'Số dư', dataIndex: 'balance_amount', align: 'right', render: renderMoney },
                {
                    title: 'Trạng thái', dataIndex: 'status', render: (value, row) => {
                        if (row.reversal_of_id) return <Tag color="blue">Giao dịch hoàn tác đã hoàn tất</Tag>;
                        const status = transferStatus[value] || { color: 'default', label: 'Đang xử lý' };
                        return <Tag color={status.color}>{status.label}</Tag>;
                    }
                },
                { title: 'Thời gian', dataIndex: 'effective_at', render: value => moment(value).format('DD/MM/YYYY HH:mm') },
                {
                    title: '', render: (_, row) => canReverse && Number(row.from_customer_id) === Number(customerId)
                        && !row.reversal_of_id && row.status !== 'REVERSED' ? <Button size="small" danger onClick={() => reverse(row)}>Hoàn tác phần còn lại</Button> : null
                },
            ]} />
    </Modal>;
}
